import { useEffect, useRef, useState } from 'react';

export type ApiState<T> =
  | { status: 'loading'; data: null; error: null }
  | { status: 'ready'; data: T; error: null }
  | { status: 'error'; data: null; error: string };

/**
 * Hämtar data från API:et och avbryter pågående anrop när komponenten
 * lämnas eller nyckeln ändras.
 */
export function useApi<T>(key: string, loader: (signal: AbortSignal) => Promise<T>): ApiState<T> {
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const [state, setState] = useState<ApiState<T>>({ status: 'loading', data: null, error: null });

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading', data: null, error: null });

    loaderRef
      .current(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          setState({ status: 'ready', data, error: null });
        }
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          status: 'error',
          data: null,
          error: error instanceof Error ? error.message : 'Innehållet kunde inte hämtas.'
        });
      });

    return () => controller.abort();
  }, [key]);

  return state;
}
